import React, { useEffect, useState } from "react";
import Layout from "../../../../Components/Common/Layout.js";
import DashLayout from "../../../../Components/Admin/AdminDashLayout.js";
import { useDispatch, useSelector } from "react-redux";
import { Input, Spin, Table, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import InventoryForm from "./InventoryForm.js";
import { fetchInventory } from "../../../../Store/Slices/Finance/inventory/inventory.thunk.js";
import Sidebar from "../../../../Components/Common/Sidebar.js";
import useNavHeading from "../../../../Hooks/CommonHooks/useNavHeading .js";
import { MdEdit, MdRemoveRedEye } from "react-icons/md";
import { GoPlus } from "react-icons/go";
import { render } from "react-dom";
import { useTranslation } from "react-i18next";
import ProtectedAction from "../../../../Routes/ProtectedRoutes/ProtectedAction.js";
import { PERMISSIONS } from "../../../../config/permission.js";
import TableShimmer from "../../../../Components/Common/TableShimmer.jsx";

const InventoryList = () => {
  const dispatch = useDispatch();
  const role = useSelector((store) => store.common.auth.role);
  const { inventories, loading, filteredCount, total, totalPages, page } = useSelector(
    (store) => store.admin.inventory
  );
  const { t } = useTranslation("prtFinance");
  const [searchText, setSearchText] = useState("");
  const [inventoryStatus, setInventoryStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  useNavHeading(role, `Inventory`);

  useEffect(() => {
    dispatch(
      fetchInventory({
        status: inventoryStatus,
        search: searchText,
        page: currentPage,
        limit: pageSize,
      })
    );
  }, [dispatch, inventoryStatus, searchText, currentPage, pageSize]);
const handleSearch = (e)=>{
  setCurrentPage(1);
  setSearchText(e.target.value)
}
  const columns = [
    {
      title: t("Item Name"),
      dataIndex: "itemName",
      key: "itemName",
      ellipsis: true,
      width: 100,
    },
    {
      title: t("SKU"),
      dataIndex: "SKU",
      key: "SKU",
      ellipsis: true,
      width: 100,
    },
    {
      title: t("Current Stock"),
      dataIndex: "quantity",
      key: "quantity",
      width: 80,
    },
    {
      title: t("Unit Price"),
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 100,
    },
    {
      title: t("Status"),
      dataIndex: "inventoryStatus",
      key: "inventoryStatus",
      width: 50,
    },
    {
      title: t("Expiry Date"),
      dataIndex: "expireDate",
      key: "expireDate",
      render: (value) => <p>{value?.slice(0, 10) || "-"}</p>,
      width: 100,
    },

    {
      title: t("Low Stock Alert"),
      render: (_, record) => {
        return (
          <p
            className={`text-${record.lowStockAlert ? "green" : "red"
              }-500 font-bold px-14`}
          >
            {record.lowStockAlert ? "Yes" : "No"}
          </p>
        );
      },
      width: 100,
    },
    {
      title: t("Action"),
      render: (_, record) => (
        <div className="flex flex-row gap-4">
          <ProtectedAction requiredPermission={PERMISSIONS.EDIT_INVENTORY_ITEM}>
            <span
              className="text-xs text-blue-600 cursor-pointer"
              onClick={() => {
                setSelectedInventory(record);
                setIsModalVisible(true);
              }}
              title="Edit"
            >
              <MdEdit size={20} />
            </span>
          </ProtectedAction>
          <span
            className="text-xs text-blue-600 cursor-pointer"
            onClick={() => {
              setSelectedInventory({ mode: "view", ...record });
              setIsModalVisible(true);
            }}
            title="View"
          >
            <MdRemoveRedEye size={20} />
          </span>
        </div>
      ),
      width: 80,
    },
  ];

  return (
    <Layout title={`Inventory | Office Flow`}>
      <DashLayout>
        <div className="p-4">
          <div className="flex w-full flex-row items-center justify-between mb-4 gap-4">
            <div className="flex w-full flex-row items-center gap-4">
              <Input
                placeholder={t("Search by name, SKU...")}
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => handleSearch(e)}
                allowClear
                className="w-80 py-1"
              />
              <Select
                className=" px-1  w-[10rem]"
                value={inventoryStatus}
                onChange={(value) => setInventoryStatus(value)}
                placeholder={t("Select Status")}
              >
                <Select.Option value="">{t("All")}</Select.Option>
                <Select.Option value="Available">
                  {t("Available")}
                </Select.Option>
                <Select.Option value="Out Of Stock">
                  {t("Out Of Stock")}
                </Select.Option>
                <Select.Option value="Damaged">{t("Damaged")}</Select.Option>
              </Select>
            </div>
            <div className="">
              <ProtectedAction
                requiredPermission={PERMISSIONS.ADD_NEW_INVENTORY_ITEM}
              >
                <button
                  onClick={() => {
                    setSelectedInventory(null);
                    setIsModalVisible(true);
                  }}
                  className="btn-gradient text-white flex flex-row items-center p-2 text-sm w-32"
                >
                  <GoPlus />
                  {t("Add New Item")}
                </button>
              </ProtectedAction>
            </div>
          </div>
          {loading ? (
            <TableShimmer columns={8} rows={15} />
          ) : (
            <Table
              dataSource={inventories}
              columns={columns}
              size="small"
              pagination={{
                current: currentPage,
                total: filteredCount,
                pageSize: pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["5", "10", "20", "50"],
                size: "small",
                showTotal: (total) =>
                  `Page ${currentPage} of ${totalPages} | Total ${total} records`,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                  dispatch(
                    fetchInventory({
                      inventoryStatus,
                      search: searchText,
                      page,
                      limit: size,
                    })
                  );
                },
              }}
            />
          )}
        </div>
        <Sidebar
          title={
            selectedInventory ? t("Item Full Information") : t("Add Inventory")
          }
          width="50%"
          isOpen={isModalVisible}
          onClose={handleModalClose}
        >
          <InventoryForm
            visible={isModalVisible}
            onClose={handleModalClose}
            editData={selectedInventory}
          />
        </Sidebar>
      </DashLayout>
    </Layout>
  );
};

export default InventoryList;
